import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import ConversionForm from '../../components/Configuration/ConversionForm';
import { getConversions, deleteConversion } from '../../services/conversionsService';
import { getUnits } from '../../services/unitsService';

const Conversions = () => {
  const [conversions, setConversions] = useState([]);
  const [allConversions, setAllConversions] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromUnitFilter, setFromUnitFilter] = useState('all');
  const [toUnitFilter, setToUnitFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingConversion, setEditingConversion] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [conversionToDelete, setConversionToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);

  // Cargar conversiones y unidades desde el backend (solo una vez)
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar conversiones y unidades en paralelo
      const [conversionsResponse, unitsResponse] = await Promise.all([
        getConversions(),
        getUnits({ active_only: true })
      ]);
      
      if (conversionsResponse.success) {
        setAllConversions(conversionsResponse.data || []);
      }
      
      if (unitsResponse.success) {
        setUnits(unitsResponse.data || []);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar los datos',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadData();
    }
  }, []);

  // Filtrar conversiones localmente en el frontend
  const filteredConversions = allConversions.filter(conversion => {
    const matchesFromUnit = fromUnitFilter === 'all' || 
      conversion.from_unit_id === parseInt(fromUnitFilter);
    
    const matchesToUnit = toUnitFilter === 'all' || 
      conversion.to_unit_id === parseInt(toUnitFilter);
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && conversion.is_active) ||
      (statusFilter === 'inactive' && !conversion.is_active);

    return matchesFromUnit && matchesToUnit && matchesStatus;
  });

  // Calcular estadísticas
  const totalConversions = filteredConversions.length;
  const activeConversions = filteredConversions.filter(c => c.is_active).length;
  const inactiveConversions = filteredConversions.filter(c => !c.is_active).length;

  const handleDeleteClick = (conversion) => {
    setConversionToDelete(conversion);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!conversionToDelete) return;
    
    try {
      const response = await deleteConversion(conversionToDelete.id);
      
      if (response.success) {
        setAllConversions(prev => prev.filter(c => c.id !== conversionToDelete.id));
        setToast({
          isOpen: true,
          message: 'Conversión eliminada exitosamente',
          type: 'success'
        });
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar la conversión',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar conversión:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la conversión',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setConversionToDelete(null);
    }
  };

  const handleFormSuccess = () => {
    // Recargar todas las conversiones desde el backend
    loadData();
    setToast({
      isOpen: true,
      message: editingConversion ? 'Conversión actualizada exitosamente' : 'Conversión creada exitosamente',
      type: 'success'
    });
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit ? `${unit.name} (${unit.symbol})` : 'N/A';
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Conversiones de Unidades</h1>
              <p className="text-gray-600 mt-1">Gestione las tasas de conversión entre unidades de medida</p>
            </div>
            <button
              onClick={() => {
                setEditingConversion(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nueva Conversión
            </button>
          </div>

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Conversiones</span>
          </nav>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Filtro por Unidad Origen */}
              <select
                value={fromUnitFilter}
                onChange={(e) => setFromUnitFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todas las Unidades Origen</option>
                {units.map(unit => (
                  <option key={unit.id} value={unit.id}>{unit.name} ({unit.symbol})</option>
                ))}
              </select>

              {/* Filtro por Unidad Destino */}
              <select
                value={toUnitFilter}
                onChange={(e) => setToUnitFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todas las Unidades Destino</option>
                {units.map(unit => (
                  <option key={unit.id} value={unit.id}>{unit.name} ({unit.symbol})</option>
                ))}
              </select>

              {/* Filtro por Estado */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todos los Estados</option>
                <option value="active">Activas</option>
                <option value="inactive">Inactivas</option>
              </select>

              {/* Botón Limpiar */}
              <button
                onClick={() => {
                  setFromUnitFilter('all');
                  setToUnitFilter('all');
                  setStatusFilter('all');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total de Conversiones</div>
            <div className="text-2xl font-bold text-gray-800">{totalConversions}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Conversiones Activas</div>
            <div className="text-2xl font-bold text-green-600">{activeConversions}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Conversiones Inactivas</div>
            <div className="text-2xl font-bold text-red-600">{inactiveConversions}</div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              Cargando conversiones...
            </div>
          ) : filteredConversions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {fromUnitFilter !== 'all' || toUnitFilter !== 'all' || statusFilter !== 'all'
                ? 'No se encontraron conversiones con los filtros aplicados' 
                : 'No hay conversiones registradas'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unidad Origen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unidad Destino
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cálculo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descripción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredConversions.map((conversion) => (
                    <tr key={conversion.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {conversion.from_unit?.name || getUnitName(conversion.from_unit_id)}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({conversion.from_unit?.symbol || ''})
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {conversion.to_unit?.name || getUnitName(conversion.to_unit_id)}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({conversion.to_unit?.symbol || ''})
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">1</span>
                          <span className="text-sm text-gray-600">
                            {conversion.from_unit?.symbol || ''}
                          </span>
                          <span className="text-sm font-bold text-teal-600">=</span>
                          <span className="text-sm font-mono font-medium text-teal-600">
                            {parseFloat(conversion.conversion_rate).toLocaleString('es-ES', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 4
                            })}
                          </span>
                          <span className="text-sm text-gray-600">
                            {conversion.to_unit?.symbol || ''}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {conversion.from_unit?.name || ''} → {conversion.to_unit?.name || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-500 max-w-xs truncate block">
                          {conversion.description || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          conversion.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {conversion.is_active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setEditingConversion(conversion);
                            setIsFormOpen(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                          title="Editar"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteClick(conversion)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de Formulario */}
        <ConversionForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingConversion(null);
          }}
          conversion={editingConversion}
          units={units}
          onSuccess={handleFormSuccess}
        />

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setConversionToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Conversión"
          message={`¿Estás seguro de que deseas eliminar esta conversión? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Conversions;

